import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-webapi/commerce-webapi/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "create-and-manage-grouped-products-tutorial"
    }}>{`Create and manage grouped products tutorial`}</h1>
    <p>{`A grouped product consists of simple standalone products that are presented as a group. A group can contain variations of a single product or a collection of products that are to be sold together.`}</p>
    <p>{`This tutorial describes how you can use the Adobe Commerce REST API to create and manage grouped products.`}</p>
    <h3 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h3>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Install a Commerce instance with sample data. The sample data defines a functional store, called Luma, that sells fitness clothing and accessories.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Install a REST client. You can use any REST client to send calls to Commerce. `}<a parentName="p" {...{
            "href": "https://www.getpostman.com/"
          }}>{`Postman`}</a>{` is recommended.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Obtain an admin authorization token. All calls in this tutorial require administrator privileges. See `}<a parentName="p" {...{
            "href": "/commerce/webapi/rest/tutorials/prerequisite-tasks/"
          }}>{`Generate the admin token`}</a>{` for more information.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "other-resources"
    }}>{`Other resources`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/commerce/webapi/rest/tutorials/index/"
        }}>{`REST Tutorials`}</a>{` provides additional information about completing any Commerce REST tutorial.`}</li>
    </ul>
    <h2 {...{
      "id": "1-create-an-empty-grouped-product"
    }}>{`1. Create an empty grouped product`}</h2>
    <p>{`The first step is to create the grouped product container. In the next step, we'll add individual products to the grouped product.`}</p>
    <h3 {...{
      "id": "endpoint"
    }}>{`Endpoint`}</h3>
    <p><inlineCode parentName="p">{`POST V1/products`}</inlineCode></p>
    <h3 {...{
      "id": "payload"
    }}>{`Payload`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`attribute_set_id: 11`}</inlineCode>{` line corresponds to gear. The value of `}<inlineCode parentName="p">{`4`}</inlineCode>{` for the `}<inlineCode parentName="p">{`visibility`}</inlineCode>{` attribute indicates the product will be displayed on the storefront and can be searched.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`    {
       "product":{
          "sku":"new-grouped",
          "name":"New Grouped Product",
          "attribute_set_id":11,
          "type_id":"grouped",
          "visibility":4
       }
    }
`}</code></pre>
    <h3 {...{
      "id": "response"
    }}>{`Response`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
    "id": 2053,
    "sku": "new-grouped",
    "name": "New Grouped Product",
    "attribute_set_id": 11,
    "status": 1,
    "visibility": 4,
    "type_id": "grouped",
    "created_at": "2019-08-15 17:51:13",
    "updated_at": "2019-08-15 17:51:13",
    "extension_attributes": {
        "website_ids": [
            1
        ],
        "stock_item": {
            "item_id": 2053,
            "product_id": 2053,
            "stock_id": 1,
            "qty": 0,
            "is_in_stock": false,
            "is_qty_decimal": false,
            "show_default_notification_message": false,
            "use_config_min_qty": true,
            "min_qty": 0,
            "use_config_min_sale_qty": 1,
            "min_sale_qty": 1,
            "use_config_max_sale_qty": true,
            "max_sale_qty": 10000,
            "use_config_backorders": true,
            "backorders": 0,
            "use_config_notify_stock_qty": true,
            "notify_stock_qty": 1,
            "use_config_qty_increments": true,
            "qty_increments": 0,
            "use_config_enable_qty_inc": true,
            "enable_qty_increments": false,
            "use_config_manage_stock": true,
            "manage_stock": true,
            "low_stock_date": null,
            "is_decimal_divided": false,
            "stock_status_changed_auto": 0
        }
    },
    "product_links": [],
    "options": [],
    "media_gallery_entries": [],
    "tier_prices": [],
    "custom_attributes": [
        {
            "attribute_code": "options_container",
            "value": "container2"
        },
        {
            "attribute_code": "url_key",
            "value": "new-grouped-product"
        },
        {
            "attribute_code": "required_options",
            "value": "0"
        },
        {
            "attribute_code": "has_options",
            "value": "0"
        },
        {
            "attribute_code": "category_ids",
            "value": []
        }
    ]
}
`}</code></pre>
    <h2 {...{
      "id": "2-populate-the-grouped-product-with-simple-products"
    }}>{`2. Populate the grouped product with simple products`}</h2>
    <p>{`Now that we have created a grouped product, we need to add simple items to it. In this example, we add three types of backpacks.`}</p>
    <h3 {...{
      "id": "endpoint-1"
    }}>{`Endpoint`}</h3>
    <p><inlineCode parentName="p">{`POST V1/products/new-grouped/links`}</inlineCode></p>
    <h3 {...{
      "id": "payload-1"
    }}>{`Payload`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`    {
       "items":[
          {
             "sku":"new-grouped",
             "link_type":"associated",
             "linked_product_sku":"24-WB01",
             "linked_product_type":"simple",
             "position":1,
             "extension_attributes":{
                "qty":1
             }
          },
          {
             "sku":"new-grouped",
             "link_type":"associated",
             "linked_product_sku":"24-WB02",
             "linked_product_type":"simple",
             "position":2,
             "extension_attributes":{
                "qty":1
             }
          },
          {
             "sku":"new-grouped",
             "link_type":"associated",
             "linked_product_sku":"24-WB05",
             "linked_product_type":"simple",
             "position":3,
             "extension_attributes":{
                "qty":1
             }
          }
       ]
    }
`}</code></pre>
    <h3 {...{
      "id": "response-1"
    }}>{`Response`}</h3>
    <p><inlineCode parentName="p">{`true`}</inlineCode></p>
    <h2 {...{
      "id": "3-add-another-simple-product-to-the-grouped-product"
    }}>{`3. Add another simple product to the grouped product`}</h2>
    <p>{`This step uses the `}<inlineCode parentName="p">{`PUT /V1/products/new-grouped/links`}</inlineCode>{` endpoint to add an item to the grouped product.`}</p>
    <h3 {...{
      "id": "endpoint-2"
    }}>{`Endpoint`}</h3>
    <p><inlineCode parentName="p">{`PUT /V1/products/new-grouped/links`}</inlineCode></p>
    <h3 {...{
      "id": "payload-2"
    }}>{`Payload`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`    {
       "entity":{
          "sku":"new-grouped",
          "link_type":"associated",
          "linked_product_sku":"24-UG01",
          "linked_product_type":"simple",
          "position":4,
          "extension_attributes":{
             "qty":1
          }
       }
    }
`}</code></pre>
    <h3 {...{
      "id": "response-2"
    }}>{`Response`}</h3>
    <p><inlineCode parentName="p">{`true`}</inlineCode></p>
    <InlineAlert variant="success" slots="text" mdxType="InlineAlert" />
    <p>{`You also can use the `}<inlineCode parentName="p">{`DELETE`}</inlineCode>{` endpoint to delete a simple product from the group product:
`}<inlineCode parentName="p">{`DELETE /V1/products/{sku}/links/{type}/{linkedProductSku}`}</inlineCode></p>
    <h2 {...{
      "id": "verify-the-steps"
    }}>{`Verify the steps`}</h2>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`Log into the Admin.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Select `}<strong parentName="p">{`Catalog > Products`}</strong>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Click on the `}<strong parentName="p">{`New Grouped Product`}</strong>{` grouped product and expand the `}<strong parentName="p">{`Grouped Products`}</strong>{` section.`}</p>
        <p parentName="li"><span parentName="p" {...{
            "className": "gatsby-resp-image-wrapper",
            "style": {
              "position": "relative",
              "display": "block",
              "marginLeft": "auto",
              "marginRight": "auto",
              "maxWidth": "1043px"
            }
          }}>{`
      `}<span parentName="span" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "47.1875%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "display": "block",
                "transition": "opacity 0.5s 0.5s",
                "pointerEvents": "none"
              }
            }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/commerce/webapi/static/14afb9bf33df0bcdce36421caef1a851/5530d/new-grouped-product.webp 320w", "/commerce/webapi/static/14afb9bf33df0bcdce36421caef1a851/0c8fb/new-grouped-product.webp 640w", "/commerce/webapi/static/14afb9bf33df0bcdce36421caef1a851/39914/new-grouped-product.webp 1043w"],
                "sizes": "(max-width: 1043px) 100vw, 1043px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/commerce/webapi/static/14afb9bf33df0bcdce36421caef1a851/dd4a7/new-grouped-product.png 320w", "/commerce/webapi/static/14afb9bf33df0bcdce36421caef1a851/0f09e/new-grouped-product.png 640w", "/commerce/webapi/static/14afb9bf33df0bcdce36421caef1a851/0b8c0/new-grouped-product.png 1043w"],
                "sizes": "(max-width: 1043px) 100vw, 1043px",
                "type": "image/png"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/commerce/webapi/static/14afb9bf33df0bcdce36421caef1a851/0b8c0/new-grouped-product.png",
                "alt": "New grouped product",
                "title": "New grouped product",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "opacity": "0",
                  "transition": "opacity 0.5s",
                  "color": "inherit",
                  "boxShadow": "inset 0px 0px 0px 400px none",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
    `}</span></p>
      </li>
    </ol>
    <h2 {...{
      "id": "add-a-grouped-product-to-a-cart"
    }}>{`Add a grouped product to a cart`}</h2>
    <p>{`Customers can now add this grouped products to their carts, as shown below.`}</p>
    <p>{`Refer to the `}<a parentName="p" {...{
        "href": "/commerce/webapi/rest/tutorials/orders/"
      }}>{`Order processing tutorial`}</a>{` for more information about how to add items to a cart with REST.`}</p>
    <h3 {...{
      "id": "endpoint-3"
    }}>{`Endpoint`}</h3>
    <p><inlineCode parentName="p">{`POST /V1/carts/mine/items`}</inlineCode></p>
    <h3 {...{
      "id": "payload-3"
    }}>{`Payload`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
 "cartItem": {
   "sku": "new-grouped",
   "qty": 1,
   "quote_id": "3"
   }
}
`}</code></pre>
    <h3 {...{
      "id": "response-3"
    }}>{`Response`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
   "item_id": 5,
   "sku": "24-WB01",
   "qty": 1,
   "name": "Voyage Yoga Bag",
   "price": 32,
   "product_type": "grouped",
   "quote_id": "3"
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      